import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";

import { EditionInputsContainer, Footer, UploadButton } from "../EditionModal.styled";
import UploadInput from "./upload-input/UploadInput";
import EditionOptionSelect from "../edition-option-select/EditionOptionSelect";
import SliderPreview from "./slider-preview/SliderPreview";
import { ActionsAdAssistant } from "../../../../Actions/ActionsAdAssistant/ActionsAdAssistant";
import AdAssistantStore from "../../AdAssistantStore";
import { withRefluxStore } from "../../../../Stores";
import Actions from "../../../../Actions";
import usePrevious from "../../../../hooks/usePrevious";
import { FlexWrapper } from "../../AdAssistant.styled";
import { SeeAllImagesButton } from "./ImageEdition.styled";
import PopupPreview from "../../../global-media-library/components/popup-preview/PopupPreview";

const ImageEdition = ({
  saveNewImage,
  selectedCurrentAdAsset,
  // Store
  uploadedImages,
  oldUploadedImages,
  campaignData,
  organization
}) => {
  const { t } = useTranslation();

  const [selectedImageEdition, setSelectedImageEdition] = useState(null);
  const [isLoadingImage, setIsLoadingImage] = useState(true);
  const [editionOptionSelected, setEditionOptionSelected] = useState("recommendations");
  const [showAllImagesPopup, setShowAllImagesPopup] = useState(false);

  const [imageHasChanged, setImageHasChanged] = useState(false);
  const prevImageHasChanged = usePrevious(imageHasChanged);

  const { mediaArray, companyId } = campaignData;

  // Set selected image edition with the current ad asset
  useEffect(() => {
    if (selectedCurrentAdAsset) {
      setSelectedImageEdition(selectedCurrentAdAsset);
    }
  }, [selectedCurrentAdAsset]);

  const handleSaveChanges = useCallback(() => {
    if (imageHasChanged && !prevImageHasChanged && selectedImageEdition) {
      saveNewImage(selectedImageEdition);
    }
    Actions.addNotification(t("Saved correctly"));
  }, [imageHasChanged, prevImageHasChanged, selectedImageEdition, t, saveNewImage]);

  useEffect(() => {
    if (imageHasChanged && !prevImageHasChanged && selectedImageEdition) {
      setImageHasChanged(false);
      handleSaveChanges();
    }
  }, [imageHasChanged, prevImageHasChanged, handleSaveChanges, selectedImageEdition]);

  useEffect(() => {
    ActionsAdAssistant.getCloudinaryImagesByCompanyId(companyId);
    ActionsAdAssistant.getOldUploadedImages(
      organization?.name,
      campaignData?.companyName,
      organization?.id,
      null
    );
  }, [companyId, organization, campaignData]);

  const handleNewAssetUpload = (newAsset) => {
    handleAssetSelection(newAsset);

    setTimeout(() => {
      ActionsAdAssistant.getCloudinaryImagesByCompanyId(companyId);
    }, 2000);
  };

  const handleEditionOptionSelected = (optionSelected) => {
    setEditionOptionSelected(optionSelected.value);
  };

  const handleAssetSelection = (newAssetURL) => {
    setSelectedImageEdition(newAssetURL);
    setImageHasChanged(true);
  };

  const allImages = Array.from(new Set([...uploadedImages, ...oldUploadedImages]));

  //TEMP ASSISTANT
  const formattedMediaArray = mediaArray.map((media) => {
    return media.url;
  });

  return (
    <EditionInputsContainer gap={15} direction={"column"} align={"flex-start"}>
      <AnimatePresence>
        <motion.div
          key={isLoadingImage}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          style={{ width: "100%", display: "flex", flexDirection: "column", gap: "24px" }}
        >
          <FlexWrapper justify={"space-between"} align={"center"} width={"100%"} gap={10}>
            <div style={{ flex: 1 }}>
              <EditionOptionSelect
                emitOptionSelected={handleEditionOptionSelected}
                optionSelected={editionOptionSelected}
              />
            </div>
            {editionOptionSelected === "mediaLibrary" && (
              <SeeAllImagesButton onClick={setShowAllImagesPopup}>
                {t("See all")}
              </SeeAllImagesButton>
            )}
          </FlexWrapper>

          {editionOptionSelected === "recentUploads" && (
            <>
              <SliderPreview
                assets={uploadedImages}
                imageSelected={selectedImageEdition}
                emitNewImageSelection={handleAssetSelection}
              />
              <UploadInput
                emitNewImageUpload={handleNewAssetUpload}
                orgaId={organization?.id}
                companyId={companyId}
                emitIsUploading={setIsLoadingImage}
              />
            </>
          )}

          {editionOptionSelected === "mediaLibrary" && (
            <>
              <SliderPreview
                assets={allImages}
                imageSelected={selectedImageEdition}
                emitNewImageSelection={handleAssetSelection}
              />

              <PopupPreview
                isOpen={showAllImagesPopup}
                setIsOpen={() => setShowAllImagesPopup(!showAllImagesPopup)}
                companyId={companyId}
                companyName={campaignData?.companyName}
                campaignData={campaignData}
                emitNewImageSelection={handleAssetSelection}
              />
            </>
          )}

          {editionOptionSelected === "recommendations" && (
            <SliderPreview
              assets={formattedMediaArray}
              imageSelected={selectedImageEdition}
              emitNewImageSelection={handleAssetSelection}
            />
          )}
        </motion.div>
      </AnimatePresence>

      <Footer>
        <UploadButton height={30} onClick={() => setEditionOptionSelected("recentUploads")}>
          {t("Upload")}
        </UploadButton>
      </Footer>
    </EditionInputsContainer>
  );
};

export default withRefluxStore(
  ImageEdition,
  [AdAssistantStore],
  ["uploadedImages", "oldUploadedImages", "campaignData", "organization"]
);
